import React, { useEffect, useRef, useState } from "react"
import TagManager from "react-gtm-module"
import { useNavigate } from "react-router-dom"

import BlurWrapper from "@fuse/sharedComponents/blurWrapper"
import SolidBtn from "@fuse/sharedComponents/buttons/SolidBtn"
import { EVENT_STATUS, EventDocData } from "@letshang/constants"
import { get, isEmpty } from "lodash-es"
import { useRecoilState } from "recoil"
import { ROUTES } from "src/App"

import { landingGifCarouselData } from "@fuse/recoil/events"
import { userState } from "@fuse/recoil/signup"

import { ELogGTMEvent } from "@firebaseConfig/analyticsUtil"

import { getEventDetails } from "@api/firebaseWrappedApis"

import { fetchEventConfig } from "@fuse/pages/landingPage/utils"

export interface EventAssets {
  url?: string
  search_terms?: string[]
  action: string
  type: string
  message?: string
  deeplink?: string
  eventId?: string
  flyerImage?: string
  eventName?: string
  eventDescription?: string
}

type GifCarouselProps = {
  eventData?: EventDocData
  onOpenDownloadAppModal?: () => void
}

const SkeletonLoader: React.FC = () => {
  return (
    <div className="relative w-full overflow-hidden rounded-2xl h-[40vh] min-h-96 bg-gray-700 animate-pulse">
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-gray-500/30 to-transparent animate-shimmer"></div>

      <div className="flex md:hidden h-full">
        <div className="w-full bg-gray-600 rounded-2xl relative">
          <div className="absolute bottom-0 left-0 right-0 h-[30%] bg-gray-700/80 rounded-br-2xl rounded-bl-2xl p-4">
            <div className="flex items-center justify-between h-full">
              <div className="flex-1 space-y-2">
                <div className="h-3 bg-gray-500 rounded w-3/4"></div>
                <div className="h-3 bg-gray-500 rounded w-1/2"></div>
              </div>
              <div className="ml-4 h-10 w-28 bg-gray-500 rounded"></div>
            </div>
          </div>
        </div>
      </div>

      <div className="hidden md:flex h-full">
        <div className="w-6/12 bg-gray-600 rounded-tl-2xl rounded-bl-2xl relative">
          <div className="absolute inset-0 bg-gray-700/80 rounded-tl-2xl rounded-bl-2xl p-4 flex flex-col justify-center">
            <div className="space-y-4">
              <div className="h-6 bg-gray-500 rounded w-3/4"></div>
              <div className="h-6 bg-gray-500 rounded w-1/2"></div>
              <div className="h-12 w-48 bg-gray-500 rounded mt-8"></div>
            </div>
          </div>
        </div>
        <div className="w-6/12 bg-gray-600 rounded-tr-2xl rounded-br-2xl"></div>
      </div>
    </div>
  )
}

const GifCarousel: React.FC<GifCarouselProps> = ({
  eventData,
  onOpenDownloadAppModal,
}) => {
  const navigate = useNavigate()
  const [carouselData, setCarouselData] = useRecoilState(landingGifCarouselData) // Use Recoil State
  const [loading, setLoading] = useState(null)
  const [user] = useRecoilState(userState)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [extendedData, setExtendedData] = useState<any[]>([])
  const [isDataReady, setIsDataReady] = useState(false)

  useEffect(() => {
    if (carouselData.length > 0) {
      const currentMonth = new Date()
        .toLocaleString("default", {
          month: "long",
        })
        .toLowerCase()
      let currentMonthData = carouselData.filter(
        (slide) => slide.month === currentMonth,
      )
      if (currentMonthData.length === 0) {
        currentMonthData = carouselData.filter(
          (slide) => slide.month === "default",
        )
      }
      setExtendedData(Array(10).fill(currentMonthData).flat())
      setIsDataReady(true)
    }
  }, [carouselData])

  const uid = get(user, "uid", "")

  const getFeaturedEventDetails = async (allFeatureEventsId: string[]) => {
    try {
      const eventDetailsArray = await Promise.all(
        allFeatureEventsId.map(async (id: string) => {
          const result = await getEventDetails({ eventId: id, uid })
          return get(result, "success", false) &&
            get(result, "data.eventDocData.eventStatus") === EVENT_STATUS.OPEN
            ? { ...get(result, "data.eventDocData", {}), eventId: id }
            : null
        }),
      )

      return eventDetailsArray.filter(Boolean) // Filters out null/undefined values
    } catch (error) {
      console.error("Error fetching event details:", error)
      return []
    }
  }

  const fetchEventAssets = async () => {
    try {
      // throw new Error("check")
      const eventAssets = await fetchEventConfig()
      const allFeatureEventsId = get(eventAssets, "events", [])?.map(
        (element: { deeplink: string }) => {
          return get(element, "deeplink", "").split("/").pop() as string
        },
      )

      const suggestions = get(eventAssets, "featured-assets", [])

      const featuredEventDetails =
        await getFeaturedEventDetails(allFeatureEventsId)
      const data =
        !isEmpty(eventData) &&
        get(eventData, "eventStatus") === EVENT_STATUS.OPEN
          ? [eventData, ...suggestions]
          : [...suggestions]

      const carouselList = !isEmpty(featuredEventDetails)
        ? [...data, ...featuredEventDetails]
        : [...data]
      // const carouselData = []
      if (carouselList?.length > 0) {
        setCarouselData(carouselList)
      }
    } catch (error) {
      console.error("Error fetching event assets:", error)
    }
  }
  useEffect(() => {
    fetchEventAssets()
  }, [])

  const onButtonPress = (eventType: string, eventId: string) => {
    // Send custom event to GTM
    TagManager.dataLayer({
      dataLayer: {
        event: ELogGTMEvent.BUTTON_CLICK,
        // Metadata about the button that was clicked and the user
        button_name: "Create event",
        user_id: uid,
      },
    })

    if (eventType === "suggestions") {
      // onOpenDownloadLink(downloadLink)
      onOpenDownloadAppModal()
      navigate(ROUTES.CREATE_EVENT_V5)
    } else {
      navigate(ROUTES.EVENTS.replace(":eventId", eventId))
    }
  }

  useEffect(() => {
    if (!loading && isDataReady) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % extendedData?.length)
      }, 5000)

      return () => clearInterval(interval)
    }
  }, [extendedData?.length, loading, isDataReady])

  if (!isDataReady || extendedData?.length === 0) {
    return <SkeletonLoader />
  }

  return (
    <div className="relative w-full overflow-hidden rounded-2xl h-[40vh] min-h-96">
      <div
        className="flex transition-transform duration-700 ease-in-out h-full"
        style={{
          width: `${extendedData?.length * 100}%`,
          transform: `translateX(-${currentIndex * (100 / extendedData?.length)}%)`,
        }}
      >
        {extendedData?.map((slide, idx) => (
          <div
            key={idx}
            className="w-full flex-shrink-0 relative rounded-2xl overflow-hidden"
            style={{ width: `${100 / extendedData?.length}%` }}
          >
            {loading === null && (
              <div className="absolute inset-0 z-30 bg-white/20 rounded-2xl animate-pulse" />
            )}
            <div
              className={`h-full w-full flex flex-row transition-opacity duration-700 ease-in ${loading ? "opacity-0" : "opacity-100"}`}
            >
              <img
                src={slide?.url || slide?.flyerImage}
                className="hidden md:flex h-full w-6/12 object-cover rounded-tl-2xl rounded-bl-2xl"
                onLoad={() => setLoading(false)}
              />
              <img
                src={slide?.url || slide?.flyerImage}
                className="h-full object-cover rounded-br-2xl rounded-tr-2xl flex-1"
                onLoad={() => setLoading(false)}
              />
            </div>

            {/* Your overlays (mobile and desktop) */}
          </div>
        ))}
      </div>
      {!loading && (
        <>
          <BlurWrapper
            overlayOpacityDefault="5"
            className="flex md:hidden w-full rounded-br-2xl rounded-bl-2xl left-0 bottom-0 h-[30%] absolute z-50 bg-gray-600/30 text-white p-4 flex-col items-center justify-center"
          >
            <div className="flex-1 flex flex-row items-center justify-center gap-10  w-full ">
              <p className="text-sm text-left flex-1 w-full line-clamp-3">
                {get(extendedData[currentIndex], "eventName")
                  ? extendedData[currentIndex]?.eventDescription
                  : `${extendedData[currentIndex]?.message}`}
              </p>
              <SolidBtn
                size="sm"
                variant={"solid"}
                className="inline-flex text-white opacity-100 min-w-28 h-10"
                onClick={() =>
                  onButtonPress(
                    get(extendedData[currentIndex], "type", ""),
                    get(extendedData[currentIndex], "eventId", ""),
                  )
                }
              >
                {get(extendedData[currentIndex], "action", "") ===
                "create-event"
                  ? "Create event"
                  : "See event"}
              </SolidBtn>
            </div>
          </BlurWrapper>
          <BlurWrapper
            overlayOpacityDefault="5"
            className="hidden md:flex rounded-tl-2xl rounded-bl-2xl left-0 top-0 h-[40vh] min-h-96 w-6/12 absolute z-50 bg-gray-600/30 text-white p-4 flex-col gap-8 items-center justify-center"
          >
            <div className="flex flex-col items-center justify-start gap-10">
              <p className="text-2xl font-semibold mb-2 text-start flex flex-1 line-clamp-3">
                {get(extendedData[currentIndex], "eventName")
                  ? extendedData[currentIndex]?.eventDescription
                  : `${extendedData[currentIndex]?.message}`}
              </p>
              <div className="w-full text-left">
                <SolidBtn
                  size="sm"
                  variant={"solid"}
                  className="inline-flex text-white opacity-100 min-w-48 h-10 md:h-12 text-base"
                  onClick={() =>
                    onButtonPress(
                      get(extendedData[currentIndex], "type", ""),
                      get(extendedData[currentIndex], "eventId", ""),
                    )
                  }
                >
                  {get(extendedData[currentIndex], "action", "") ===
                  "create-event"
                    ? "Create event"
                    : "See event"}
                </SolidBtn>
              </div>
            </div>
          </BlurWrapper>
        </>
      )}
    </div>
  )
}

export default GifCarousel
